/**
 * AnimatorPane — dedicated 4D timeline animator for APS schedule tables.
 *
 * Opens as a modal overlay on top of the APS Viewer drawer. Renders the
 * schedule rows as a **hierarchical, collapsible WBS tree** indented by
 * the Outline Level column, lets the user pick exactly which row to start
 * the animation from (with prior rows pre-loaded as "as-built" context),
 * and exposes a play / pause / speed control surface.
 *
 * Props:
 *   - open            : whether the modal is visible
 *   - onClose         : close-handler (Escape / X / backdrop)
 *   - rows[]          : raw markdown table body-rows (string `|...|`)
 *   - onPlay(startIdx): caller fires the animation; pane closes itself
 *   - playing         : whether an animation is currently active
 *   - onStop          : called when the user clicks Stop while playing
 */
import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, ChevronRight, ChevronDown, Film } from 'lucide-react';

// Parse a single body-row into a structured object the tree can render.
// Schedule format (column order):
//   0: Activity ID | 1: WBS Level | 2: Outline Level | 3: Task Name | ...
const parseRow = (raw, idx) => {
  // Strip the surrounding empty cells caused by leading/trailing `|`.
  const cells = raw.split('|').map((c) => c.trim());
  const filtered = cells.filter((c, j) => !(j === 0 && c === '') && !(j === cells.length - 1 && c === ''));
  const activityId = filtered[0] || `Row ${idx + 1}`;
  const wbs = filtered[1] || '';
  const outlineRaw = filtered[2] || '';
  // Parse outline level — accept "1", "2", "  1", or " · 2" etc.
  const outlineMatch = String(outlineRaw).match(/(\d+)/);
  const outline = outlineMatch ? parseInt(outlineMatch[1], 10) : 1;
  // Task name — strip any LLM-inserted indent characters (em-spaces, ↳)
  // since we re-indent visually with CSS in the tree.
  const rawName = filtered[3] || '';
  const taskName = rawName.replace(/^[\s\u2003↳·\-]+/, '').trim() || rawName.trim();
  return { idx, activityId, wbs, outline: Math.max(1, Math.min(outline, 8)), taskName, raw };
};

export default function AnimatorPane({ open, onClose, rows = [], onPlay, playing = false, onStop, meter = null }) {
  const [collapsed, setCollapsed] = useState(new Set());  // set of activityIds whose children are hidden
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Parse all rows once. Defensively filter out rows that are clearly
  // not schedule activities — e.g. blank rows, rows where the Task
  // Name column is empty (catalogue tables, header artefacts, etc.).
  // The schedule-aware parseTableRows in ApsViewerPanel should already
  // pick the right table; this is a second-line guard.
  const parsed = useMemo(() => {
    const all = rows.map(parseRow);
    return all.filter((p) => p.taskName && p.taskName.length > 0
                              && p.taskName.toLowerCase() !== 'no task name'
                              && p.activityId && p.activityId !== '—');
  }, [rows]);

  // Compute "is row visible" by walking ancestors — if any ancestor (any
  // earlier row with lower outline level) is collapsed, this row is hidden.
  const visibilityMap = useMemo(() => {
    const map = new Array(parsed.length).fill(true);
    // Walk forward, track collapse-stack of ancestor indices.
    const collapsedAncestors = [];  // each: { outline, idx }
    for (let i = 0; i < parsed.length; i += 1) {
      const p = parsed[i];
      // Pop ancestors that are now at the same or higher outline level.
      while (collapsedAncestors.length
             && p.outline <= collapsedAncestors[collapsedAncestors.length - 1].outline) {
        collapsedAncestors.pop();
      }
      // If any collapsed ancestor remains in the stack, this row is hidden.
      map[i] = collapsedAncestors.length === 0;
      // If this row itself is collapsed, push it as an ancestor for descendants.
      if (collapsed.has(p.activityId)) {
        collapsedAncestors.push({ outline: p.outline, idx: i });
      }
    }
    return map;
  }, [parsed, collapsed]);

  // Does this row have any descendants (next row(s) with deeper outline)?
  const hasChildrenMap = useMemo(() => {
    const map = new Array(parsed.length).fill(false);
    for (let i = 0; i < parsed.length; i += 1) {
      const next = parsed[i + 1];
      if (next && next.outline > parsed[i].outline) map[i] = true;
    }
    return map;
  }, [parsed]);

  const toggleCollapse = (activityId) => {
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(activityId)) next.delete(activityId); else next.add(activityId);
      return next;
    });
  };

  const handlePlay = () => {
    onPlay?.(selectedIdx);
  };

  if (!open) return null;

  const remaining = parsed.length - selectedIdx;
  const selectedRow = parsed[selectedIdx];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="animator-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 26, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 14, 26, 0.98) 100%)',
            border: '1px solid rgba(122, 183, 255, 0.22)',
            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
          }}
          data-testid="animator-pane"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(122, 183, 255, 0.15)' }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg" style={{ background: 'rgba(242, 107, 31, 0.15)', border: '1px solid rgba(242, 107, 31, 0.35)' }}>
                <Film size={16} style={{ color: '#F26B1F' }} />
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-nexus-text-primary">4D Animator</div>
                <div className="text-[11px] text-nexus-text-tertiary">
                  {parsed.length} activities · pick a starting row and play
                </div>
              </div>
              {/* Credit-deduction chip — surfaces the cost of generating
                  THIS schedule so engineering customers know the burn
                  before they regenerate (a 5000-row Apex run is ~200+ cr). */}
              {meter && (() => {
                const tier = meter.model === 'claude-opus-4-8' ? 'Apex 4.8'
                  : meter.model === 'claude-opus-4-7' ? 'Ultra 4.7'
                  : meter.model === 'claude-opus-4-6' ? 'Pro 4.6'
                  : (meter.model || '').replace('claude-', '').replace(/-/g, ' ');
                const deducted = Number(meter.deducted ?? 0);
                const base = Number(meter.base_credits ?? 0);
                const mult = Number(meter.multiplier ?? 1);
                return (
                  <div className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono text-nexus-text-tertiary"
                       style={{ background: 'rgba(242,107,31,0.06)', border: '1px solid rgba(242,107,31,0.18)' }}
                       data-testid="animator-credit-meter-chip"
                       title={`This schedule cost ${deducted} credit${deducted === 1 ? '' : 's'} to generate = ⌈${meter.tokens_total || 0} tokens / 1000⌉ (${base} base) × ${mult}× (${tier})`}>
                    <span style={{ color: '#F26B1F', fontWeight: 600 }}>−{deducted} cr</span>
                    <span className="opacity-60">·</span>
                    <span>{tier}</span>
                  </div>
                );
              })()}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-nexus-bg-tertiary/50 text-nexus-text-secondary"
              title="Close (Esc)"
              data-testid="animator-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Controls strip — sticky play / stop / selected-row summary */}
          <div className="px-5 py-3 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: 'rgba(122, 183, 255, 0.10)', background: 'rgba(15, 23, 42, 0.6)' }}>
            <button
              onClick={playing ? onStop : handlePlay}
              className="inline-flex items-center gap-2 text-[12.5px] font-semibold px-4 py-2 rounded-lg text-white transition-all"
              style={{
                background: playing
                  ? 'linear-gradient(135deg, #B91C1C, #DC2626)'
                  : 'linear-gradient(135deg, #F26B1F, #FB8B3A)',
                boxShadow: playing
                  ? '0 4px 16px rgba(220, 38, 38, 0.35)'
                  : '0 4px 16px rgba(242, 107, 31, 0.35)',
              }}
              data-testid="animator-play"
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
              {playing ? 'Stop animation' : `Animate ${remaining} of ${parsed.length} rows`}
            </button>
            {!playing && selectedRow && (
              <div className="text-[12px] text-nexus-text-secondary min-w-0 truncate" data-testid="animator-selected-label">
                <span className="text-nexus-text-tertiary">Start from:</span>{' '}
                <span className="font-semibold text-nexus-text-primary">{selectedRow.activityId}</span>
                {selectedRow.wbs && <span className="text-nexus-text-tertiary"> · WBS {selectedRow.wbs}</span>}
                {selectedRow.taskName && <span className="text-nexus-text-tertiary"> · {selectedRow.taskName}</span>}
              </div>
            )}
            {selectedIdx > 0 && !playing && (
              <button
                onClick={() => setSelectedIdx(0)}
                className="text-[11px] px-2 py-1 rounded border border-nexus-border-subtle text-nexus-text-tertiary hover:text-nexus-text-primary hover:border-nexus-accent-blue/40"
                title="Reset to first row"
                data-testid="animator-reset-start"
              >
                ↺ Reset
              </button>
            )}
          </div>

          {/* Tree — scrollable WBS hierarchy */}
          <div className="flex-1 overflow-y-auto p-2" data-testid="animator-tree">
            {parsed.length === 0 && (
              <div className="text-center py-12 px-6 text-nexus-text-tertiary text-[12.5px] space-y-2">
                <div className="text-nexus-text-secondary font-semibold">No schedule activities found in this message.</div>
                <div>The 4D Animator needs a schedule table with <code className="text-nexus-accent-blue">Activity ID</code>, <code className="text-nexus-accent-blue">WBS Level</code>, <code className="text-nexus-accent-blue">Outline Level</code>, and <code className="text-nexus-accent-blue">Task Name</code> columns.</div>
                <div className="opacity-70">Generate a schedule first (Schedule mode), then re-open the Animator.</div>
              </div>
            )}
            {parsed.map((p, i) => {
              if (!visibilityMap[i]) return null;
              const isSelected = i === selectedIdx;
              const isCollapsed = collapsed.has(p.activityId);
              const hasKids = hasChildrenMap[i];
              const indentPx = (p.outline - 1) * 22;
              return (
                <div
                  key={`${p.activityId}-${i}`}
                  onClick={() => !playing && setSelectedIdx(i)}
                  className={`group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer transition-all text-[12.5px] ${isSelected ? '' : 'hover:bg-nexus-bg-tertiary/30'}`}
                  style={{
                    paddingLeft: `${indentPx + 8}px`,
                    background: isSelected ? 'rgba(242, 107, 31, 0.18)' : 'transparent',
                    border: isSelected
                      ? '1px solid rgba(242, 107, 31, 0.50)'
                      : '1px solid transparent',
                    marginBottom: '2px',
                  }}
                  data-testid={`animator-row-${i}`}
                  data-outline={p.outline}
                >
                  {hasKids ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleCollapse(p.activityId); }}
                      className="p-0.5 rounded hover:bg-nexus-bg-tertiary/40 flex-shrink-0"
                      title={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                      {isCollapsed
                        ? <ChevronRight size={12} className="text-nexus-text-tertiary" />
                        : <ChevronDown size={12} className="text-nexus-text-tertiary" />}
                    </button>
                  ) : (
                    <span className="w-[18px] flex-shrink-0" />
                  )}
                  <span
                    className="flex-shrink-0 font-mono"
                    style={{
                      color: isSelected ? '#F8A66A' : '#7AB7FF',
                      fontSize: p.outline === 1 ? '11.5px' : '11px',
                      fontWeight: p.outline === 1 ? 600 : 500,
                      letterSpacing: '0.02em',
                      minWidth: '54px',
                    }}
                  >
                    {p.activityId}
                  </span>
                  {p.wbs && (
                    <span
                      className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px]"
                      style={{
                        background: 'rgba(30, 78, 140, 0.20)',
                        color: '#CFE0FF',
                        border: '1px solid rgba(30, 78, 140, 0.30)',
                      }}
                    >
                      {p.wbs}
                    </span>
                  )}
                  <span
                    className={`flex-1 min-w-0 truncate ${isSelected ? 'text-nexus-text-primary' : 'text-nexus-text-secondary'}`}
                    style={{
                      fontWeight: p.outline === 1 ? 700 : p.outline === 2 ? 600 : 500,
                      textTransform: p.outline === 1 ? 'uppercase' : 'none',
                      letterSpacing: p.outline === 1 ? '0.04em' : 'normal',
                    }}
                    title={p.taskName}
                  >
                    {p.taskName || <span className="italic text-nexus-text-tertiary">(no task name)</span>}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer — quick tips */}
          <div className="px-5 py-2.5 border-t text-[10.5px] text-nexus-text-tertiary flex items-center gap-4" style={{ borderColor: 'rgba(122, 183, 255, 0.10)', background: 'rgba(10, 14, 26, 0.6)' }}>
            <span>Click any row to set the starting point.</span>
            <span>▸ / ▾ collapses a WBS branch.</span>
            <span>Esc closes.</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
